// ui/src/pages/admin/FrameworkConfig.tsx
import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { SectionLabel } from '../../components/common/SectionLabel';
import { RedFlagItem } from '../../components/admin/RedFlagItem';
import { CustomRedFlagEditor } from '../../components/admin/CustomRedFlagEditor';
import { FrameworkVersionHistory } from '../../components/admin/FrameworkVersionHistory';
import { FrameworkTemplateManager } from '../../components/admin/FrameworkTemplateManager';
import { useAuth } from '../../contexts/AuthContext';
import { useActiveFramework, useSaveFramework, useRedFlags } from '../../hooks/useFramework';
import { useToast } from '../../contexts/ToastContext';
import type { FrameworkConfig } from '../../types';

export function FrameworkConfigPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { data: framework, isLoading, error } = useActiveFramework();
  const { data: predefinedRedFlags } = useRedFlags();
  const saveMutation = useSaveFramework();

  const [editedConfig, setEditedConfig] = useState<FrameworkConfig | null>(null);
  const [versionNotes, setVersionNotes] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [expandedCriterion, setExpandedCriterion] = useState<string | null>(null);

  const toggleCriterion = (criterionId: string) => {
    setExpandedCriterion(prev => prev === criterionId ? null : criterionId);
  };

  // Redirect users without framework access
  if (user?.role !== 'admin' && user?.role !== 'framework-manager') {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <p className="text-wolf-300">Loading framework configuration...</p>
      </div>
    );
  }

  if (error || !framework) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-400">Failed to load framework. No framework may be configured yet.</p>
      </div>
    );
  }

  const config = editedConfig || framework;

  const handleSectionWeightChange = (sectionId: string, weight: number) => {
    setEditedConfig({
      ...config,
      sections: config.sections.map((s) =>
        s.id === sectionId ? { ...s, weight } : s
      ),
    });
  };

  const handleThresholdChange = (key: 'GREEN' | 'CONDITIONAL' | 'HOLD', value: number) => {
    setEditedConfig({
      ...config,
      thresholds: { ...config.thresholds, [key]: value },
    });
  };

  const handleCriterionWeightChange = (sectionId: string, criterionId: string, weight: number) => {
    setEditedConfig({
      ...config,
      sections: config.sections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              criteria: s.criteria.map((c) =>
                c.id === criterionId ? { ...c, weight } : c
              ),
            }
          : s
      ),
    });
  };

  const handleCriterionQuestionChange = (sectionId: string, criterionId: string, question: string) => {
    setEditedConfig({
      ...config,
      sections: config.sections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              criteria: s.criteria.map((c) =>
                c.id === criterionId ? { ...c, question } : c
              ),
            }
          : s
      ),
    });
  };

  const handleCriterionGuidanceChange = (
    sectionId: string,
    criterionId: string,
    score: string,
    text: string
  ) => {
    setEditedConfig({
      ...config,
      sections: config.sections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              criteria: s.criteria.map((c) =>
                c.id === criterionId
                  ? { ...c, guidance: { ...c.guidance, [score]: text } }
                  : c
              ),
            }
          : s
      ),
    });
  };

  const handleSave = async () => {
    if (!editedConfig) return;

    try {
      await saveMutation.mutateAsync({
        config: editedConfig,
        version_notes: versionNotes,
      });
      showToast('Framework saved successfully', 'success');
      setEditedConfig(null);
      setVersionNotes('');
      setShowSaveModal(false);
    } catch {
      showToast('Failed to save framework', 'error');
    }
  };

  const handleAddCustomRedFlag = () => {
    const newFlag = {
      id: `custom-${Date.now()}`,
      label: 'New Red Flag',
      description: '',
    };
    setEditedConfig({
      ...config,
      red_flags: {
        ...config.red_flags,
        enabled: config.red_flags?.enabled || [],
        custom: [...(config.red_flags?.custom || []), newFlag],
      },
    });
  };

  const handleUpdateCustomRedFlag = (flagId: string, updates: Partial<{ label: string; description: string }>) => {
    setEditedConfig({
      ...config,
      red_flags: {
        ...config.red_flags,
        enabled: config.red_flags?.enabled || [],
        custom: (config.red_flags?.custom || []).map((f) =>
          f.id === flagId ? { ...f, ...updates } : f
        ),
      },
    });
  };

  const handleDeleteCustomRedFlag = (flagId: string) => {
    setEditedConfig({
      ...config,
      red_flags: {
        ...config.red_flags,
        enabled: config.red_flags?.enabled || [],
        custom: (config.red_flags?.custom || []).filter((f) => f.id !== flagId),
      },
    });
  };

  const handleToggleRedFlag = (flagId: string, enabled: boolean) => {
    const currentEnabled = config.red_flags?.enabled || [];
    const newEnabled = enabled
      ? [...currentEnabled, flagId]
      : currentEnabled.filter((id) => id !== flagId);

    setEditedConfig({
      ...config,
      red_flags: {
        ...config.red_flags,
        enabled: newEnabled,
        custom: config.red_flags?.custom || [],
      },
    });
  };

  const totalWeight = config.sections.reduce((sum, s) => sum + s.weight, 0);
  const sectionWeightsValid = Math.abs(totalWeight - 1) < 0.01;

  // Calculate criterion weight totals per section
  const getCriterionWeightTotal = (section: typeof config.sections[0]) => {
    return section.criteria.reduce((sum, c) => sum + (c.weight || 0), 0);
  };

  const allCriterionWeightsValid = config.sections.every(
    (s) => Math.abs(getCriterionWeightTotal(s) - 1) < 0.01
  );

  const weightsValid = sectionWeightsValid && allCriterionWeightsValid;

  // Prepare red flags for display
  const preparedRedFlags = (() => {
    if (!predefinedRedFlags) return [];

    const enabledIds = new Set(config.red_flags?.enabled || []);

    // Predefined flags with enabled status
    const predefinedFlags = predefinedRedFlags.map((rf) => ({
      id: rf.id,
      label: rf.label,
      description: rf.description,
      guidance: rf.guidance,
      type: 'predefined' as const,
      enabled: enabledIds.has(rf.id),
    }));

    // Custom flags (always enabled if they exist)
    const customFlags = (config.red_flags?.custom || []).map((rf) => ({
      id: rf.id,
      label: rf.label,
      description: rf.description,
      guidance: undefined,
      type: 'custom' as const,
      enabled: true,
    }));

    // Sort: enabled predefined, enabled custom, disabled predefined
    const enabledPredefined = predefinedFlags.filter((f) => f.enabled).sort((a, b) => a.id.localeCompare(b.id));
    const enabledCustom = customFlags.sort((a, b) => a.id.localeCompare(b.id));
    const disabledPredefined = predefinedFlags.filter((f) => !f.enabled).sort((a, b) => a.id.localeCompare(b.id));

    return [...enabledPredefined, ...enabledCustom, ...disabledPredefined];
  })();

  return (
    <div>
      {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <SectionLabel>Admin</SectionLabel>
            <h1 className="text-3xl font-bold text-white">Framework Configuration</h1>
            {framework.created_by && (
              <p className="text-wolf-400 mt-1">
                Last modified by {framework.created_by.name} on{' '}
                {new Date(framework.created_at).toLocaleDateString()}
              </p>
            )}
          </div>
          <div className="flex gap-4">
            <Button variant="secondary" onClick={() => setShowVersionHistory(true)}>
              View History
            </Button>
            <Button variant="secondary" onClick={() => setShowTemplates(true)}>
              Templates
            </Button>
            {editedConfig && (
              <Button variant="secondary" onClick={() => setEditedConfig(null)}>
                Cancel
              </Button>
            )}
            <Button
              disabled={!editedConfig || !weightsValid}
              onClick={() => setShowSaveModal(true)}
            >
              Save & Activate
            </Button>
          </div>
        </div>

        {/* Description */}
        <Card className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Description</h2>
          <textarea
            className="w-full bg-wolf-800 border border-wolf-600 rounded p-3 text-white"
            rows={3}
            value={config.description}
            onChange={(e) =>
              setEditedConfig({ ...config, description: e.target.value })
            }
          />
        </Card>

        {/* Sections */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Sections</h2>
            <span
              className={`font-mono ${sectionWeightsValid ? 'text-action' : 'text-red-400'}`}
            >
              Section weights: {Math.round(totalWeight * 100)}%{' '}
              {sectionWeightsValid ? '✓' : '(must equal 100%)'}
            </span>
          </div>

          <div className="space-y-4">
            {config.sections.map((section) => (
              <Card key={section.id}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-white font-medium">
                      {section.id}. {section.name}
                    </h3>
                    <p className="text-wolf-400 text-sm">{section.framework}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Math.round(section.weight * 100)}
                      onChange={(e) =>
                        handleSectionWeightChange(
                          section.id,
                          parseInt(e.target.value) / 100
                        )
                      }
                      className="w-32"
                    />
                    <span className="font-mono text-action w-12 text-right">
                      {Math.round(section.weight * 100)}%
                    </span>
                  </div>
                </div>

                {/* Criteria list */}
                <div className="border-t border-wolf-700 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-wolf-400 text-sm">
                      {section.criteria.length} criteria
                    </p>
                    {(() => {
                      const criterionTotal = getCriterionWeightTotal(section);
                      const isValid = Math.abs(criterionTotal - 1) < 0.01;
                      return (
                        <span className={`text-xs font-mono ${isValid ? 'text-action' : 'text-red-400'}`}>
                          Criteria weights: {Math.round(criterionTotal * 100)}%
                          {isValid ? ' ✓' : ' (must equal 100%)'}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="space-y-1">
                    {section.criteria.map((criterion) => {
                      const isExpanded = expandedCriterion === criterion.id;
                      return (
                        <div key={criterion.id}>
                          <div className="flex items-center justify-between text-sm py-1 px-2 -mx-2 rounded hover:bg-wolf-800/50 transition-colors">
                            <button
                              type="button"
                              onClick={() => toggleCriterion(criterion.id)}
                              className="flex items-center gap-2 text-wolf-300 cursor-pointer"
                              aria-expanded={isExpanded}
                            >
                              <ChevronRight
                                className={`w-4 h-4 text-wolf-500 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                              />
                              {criterion.id}: {criterion.name}
                            </button>
                            <div className="flex items-center gap-2">
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={Math.round((criterion.weight || 0) * 100)}
                                onChange={(e) =>
                                  handleCriterionWeightChange(
                                    section.id,
                                    criterion.id,
                                    parseInt(e.target.value) / 100
                                  )
                                }
                                className="w-20"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="text-wolf-500 font-mono w-10 text-right">
                                {criterion.weight != null ? `${Math.round(criterion.weight * 100)}%` : '—'}
                              </span>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="ml-6 mt-2 mb-3 pl-4 border-l-2 border-wolf-700">
                              <div className="mb-3">
                                <label className="block text-wolf-500 text-xs mb-1">Question</label>
                                <textarea
                                  className="w-full bg-wolf-800 border border-wolf-600 rounded p-2 text-wolf-300 text-sm"
                                  rows={2}
                                  value={criterion.question || ''}
                                  onChange={(e) =>
                                    handleCriterionQuestionChange(
                                      section.id,
                                      criterion.id,
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter criterion question..."
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="block text-wolf-500 text-xs">Scoring Guidance</label>
                                {[5, 4, 3, 2, 1].map(score => (
                                  <div key={score} className="flex items-start gap-2">
                                    <span className="text-wolf-500 font-mono w-6 pt-2 text-sm">{score}:</span>
                                    <input
                                      type="text"
                                      className="flex-1 bg-wolf-800 border border-wolf-600 rounded px-2 py-1 text-wolf-300 text-sm"
                                      value={criterion.guidance?.[score.toString()] || ''}
                                      onChange={(e) =>
                                        handleCriterionGuidanceChange(
                                          section.id,
                                          criterion.id,
                                          score.toString(),
                                          e.target.value
                                        )
                                      }
                                      placeholder={`Score ${score} guidance...`}
                                    />
                                  </div>
                                ))}
                              </div>
                              {!criterion.question && !criterion.guidance && (
                                <p className="text-wolf-500 text-sm italic mt-2">No details configured</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Thresholds */}
        <Card className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Decision Thresholds</h2>
          <div className="grid grid-cols-3 gap-4">
            {(['GREEN', 'CONDITIONAL', 'HOLD'] as const).map((key) => (
              <div key={key}>
                <label className="block text-wolf-400 text-sm mb-1">{key}</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={config.thresholds[key]}
                  onChange={(e) =>
                    handleThresholdChange(key, parseInt(e.target.value) || 0)
                  }
                  className="w-full bg-wolf-800 border border-wolf-600 rounded p-2 text-white"
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Red Flags */}
        <Card className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-1">Red Flags</h2>
          <p className="text-wolf-400 text-sm mb-4">
            Flags that can block project approval
          </p>

          {/* Predefined Red Flags */}
          <div className="mb-6">
            <h4 className="text-wolf-300 text-sm font-medium mb-2">Predefined Red Flags</h4>
            <div className="space-y-2">
              {preparedRedFlags
                .filter((flag) => flag.type === 'predefined')
                .map((flag) => (
                  <RedFlagItem
                    key={flag.id}
                    flag={flag}
                    enabled={flag.enabled}
                    onToggle={handleToggleRedFlag}
                  />
                ))}
            </div>
          </div>

          {/* Custom Red Flags */}
          <div className="border-t border-wolf-700 pt-4">
            <CustomRedFlagEditor
              flags={config.red_flags?.custom || []}
              onAdd={handleAddCustomRedFlag}
              onUpdate={handleUpdateCustomRedFlag}
              onDelete={handleDeleteCustomRedFlag}
            />
          </div>
        </Card>

        {/* Save Modal */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <h2 className="text-xl font-bold text-white mb-4">Save Framework</h2>
              <label className="block text-wolf-400 text-sm mb-2">
                Version Notes (what changed?)
              </label>
              <textarea
                className="w-full bg-wolf-800 border border-wolf-600 rounded p-3 text-white mb-4"
                rows={3}
                value={versionNotes}
                onChange={(e) => setVersionNotes(e.target.value)}
                placeholder="Describe the changes..."
              />
              <div className="flex justify-end gap-4">
                <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? 'Saving...' : 'Save & Activate'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Version History Modal */}
        {showVersionHistory && (
          <FrameworkVersionHistory
            onSelectVersion={(version) => {
              // For now, just show an alert - a more complete implementation
              // would load and display the selected version
              showToast(`Selected version: ${version}`, 'success');
              setShowVersionHistory(false);
            }}
            onClose={() => setShowVersionHistory(false)}
          />
        )}

        {/* Templates Modal */}
        {showTemplates && (
          <FrameworkTemplateManager
            currentConfig={config}
            onLoadTemplate={(loadedConfig) => {
              setEditedConfig(loadedConfig);
            }}
            onClose={() => setShowTemplates(false)}
          />
        )}
    </div>
  );
}
